/*
 * AFTER EDITING THIS FILE: run `npm run deploy` in Desktop\ClaudiusCodius\GBG3.0\gbg-api. The price check / cart deals worker runs its
 * own copy of this file; until it is redeployed it refuses checkouts whose price changed (dashboard shows a red banner).
 *
 * GBG build rules - how a product title becomes a parts list (bom), and the defaults a dual-GPU
 * workstation adds. The ONE copy of this logic: the product-page configurator calls these functions
 * (sections/gbg-configurator.liquid) and so do the product scripts (gbg-api/scripts), so a script
 * always sees exactly what the configurator would load for a product.
 *
 * Pure functions: pass in the catalog (gbg-part-descriptions.json with prices applied via
 * applyPartPrices) and, for workstations, the dual book (gbg-dual-workstation.json).
 * Fit rules (GPU length, radiators, PSU step) live in gbg-compat.js.
 *
 * Loaded as window.GBGBuild (browser) or via require() (node).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.GBGBuild = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---------------- Catalog ---------------- */

  function applyPartPrices(desc, prices) {
    if (!desc || !desc.parts) return desc;
    Object.keys(desc.parts).forEach(function (slot) {
      var map = (prices && prices[slot]) || {};
      (desc.parts[slot] || []).forEach(function (part) {
        if (!part || !part.id) return;
        if (Object.prototype.hasOwnProperty.call(map, part.id)) {
          var priced = Number(map[part.id]);
          part.price = Number.isFinite(priced) ? priced : 0;
          // a price entry that isn't a number: the part can't be priced (Engine.priceProblems stops Add to Cart)
          if (!Number.isFinite(priced) || map[part.id] === '' || map[part.id] === null) part.price_missing = true;
        } else if (part.price == null) {
          part.price = 0;
          // no price at all for this part: Engine.priceProblems stops Add to Cart instead of selling it as $0
          part.price_missing = true;
        }
      });
    });
    // An SSD model can cost a different amount in the extra (second / third) drive bays: gbg-part-pricing.json "ssd_extra"
    // {modelId: price}. A model with no entry costs the same in every bay (as it always did).
    var extraMap = (prices && prices.ssd_extra) || {};
    ((desc.parts && desc.parts.ssd_gen) || []).forEach(function (part) {
      if (part && part.id && Object.prototype.hasOwnProperty.call(extraMap, part.id)) part.extra_price = Number(extraMap[part.id]) || 0;
    });
    enforceTenDollarPrices(desc);
    return desc;
  }

  // Every part price is a whole multiple of $10 (owner, 2026-10-01): a $899 base + $10 steps keeps every
  // total ending in 9 by itself, so an option's tag is exactly what the price moves (no rounding jumps on
  // color changes). A price that breaks the rule is rounded UP and reported (catalog.priceErrors +
  // console.error) - fix it in gbg-part-pricing.json / gbg-dual-workstation.json.
  function enforceTenDollarPrices(catalog) {
    var errors = catalog.priceErrors || (catalog.priceErrors = []);
    Object.keys(catalog.parts || {}).forEach(function (slot) {
      (catalog.parts[slot] || []).forEach(function (part) {
        var n = Number(part && part.price);
        if (part && part.extra_price != null && Number(part.extra_price) % 10 !== 0) {
          var upX = Math.ceil(Number(part.extra_price) / 10) * 10;
          var msgX = slot + ' ' + part.id + ' (extra drive): $' + part.extra_price + ' is not a multiple of $10 - using $' + upX;
          if (errors.indexOf(msgX) === -1) {
            errors.push(msgX);
            if (typeof console !== 'undefined') console.error('[GBG price data] ' + msgX);
          }
          part.extra_price = upX;
        }
        if (!part || !n || n % 10 === 0) return;
        var up = Math.ceil(n / 10) * 10;
        var msg = slot + ' ' + part.id + ': $' + n + ' is not a multiple of $10 - using $' + up;
        if (errors.indexOf(msg) === -1) {
          errors.push(msg);
          if (typeof console !== 'undefined') console.error('[GBG price data] ' + msg);
        }
        part.price = up;
      });
    });
  }

  function catalogSlot(slot) {
    if (/^ssd[2-9]\d*_gen$/.test(slot)) return 'ssd_gen';
    return slot;
  }

  // Unknown ids fall back to the slot's first part (and log), exactly like the configurator.
  function partById(catalog, slot, id) {
    var list = (catalog && catalog.parts && catalog.parts[catalogSlot(slot)]) || [];
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    if (id && typeof console !== 'undefined') {
      console.error(
        '[gbg] no part "' + id + '" in slot "' + slot + '" -- pricing as "' +
        ((list[0] && list[0].id) || 'nothing') + '". Check gbg-part-descriptions.json.'
      );
    }
    return list[0] || null;
  }

  // Parts the engine treats specially, by role (gbg-part-descriptions.json "part_roles"; workstation ones in
  // gbg-dual-workstation.json "defaults"/"anchors"). The engine code names no part ids, so the dashboard can rename
  // any part and these follow it.
  function role(catalog, name) {
    var roles = (catalog && catalog.part_roles) || {};
    return roles[name] == null ? '' : roles[name];
  }

  function isRole(catalog, name, id) {
    var r = role(catalog, name);
    return !!r && id === r;
  }

  // How titles are read when they name no catalog part (gbg-part-descriptions.json "title_rules").
  function titleRules(catalog) {
    return (catalog && catalog.title_rules) || {};
  }

  function psuWattsNumber(part) {
    if (!part) return 0;
    if (part.watts != null && part.watts !== '') return Number(part.watts) || 0;
    var m = String(part.label || part.short || '').match(/(\d+)\s*W/i);
    if (m) return Number(m[1]);
    m = String(part.id || '').match(/(\d{3,4})w/i);
    return m ? Number(m[1]) : 0;
  }

  /* ---------------- Title -> parts ---------------- */

  function compactText(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function matchPartFromHay(parts, hay) {
    var list = (parts || []).slice().sort(function (a, b) {
      return String(b.label || '').length - String(a.label || '').length;
    });
    var ch = compactText(hay);
    for (var i = 0; i < list.length; i++) {
      var label = String(list[i].label || '');
      if (label.length < 3) continue;
      if (hay.indexOf(label.toLowerCase()) !== -1) return list[i].id;
      var c = compactText(label);
      if (c.length >= 6 && ch.indexOf(c) !== -1) return list[i].id;
    }
    return '';
  }

  // Titles often omit the Ryzen 5/7/9 tier ("Ryzen 9600X" vs catalog "Ryzen 5 9600X").
  function matchCpuFromHay(parts, hay, catalog) {
    var compactHay = compactText(hay);
    var list = (parts || []).slice().sort(function (a, b) {
      return String(b.label || '').length - String(a.label || '').length;
    });
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      var label = String(p.label || '');
      if (label && hay.indexOf(label.toLowerCase()) !== -1) return p.id;
      var slug = compactText(p.id);
      if (slug.length >= 8 && compactHay.indexOf(slug) !== -1) return p.id;
      var core = compactText(label).replace(/^ryzen[3579]/, '');
      if (core.length >= 5 && compactHay.indexOf(core) !== -1) return p.id;
    }
    return ruleFromHay(titleRules(catalog).cpu, compactHay, false);
  }

  // First title rule whose "has" text the (compact) title contains -> its part id ("id_16gb" for 16GB titles).
  function ruleFromHay(rules, compactHay, gb16) {
    rules = rules || [];
    for (var i = 0; i < rules.length; i++) {
      var has = rules[i].has || [];
      for (var h = 0; h < has.length; h++) {
        if (has[h] && compactHay.indexOf(has[h]) !== -1) return (gb16 && rules[i].id_16gb) || rules[i].id || '';
      }
    }
    return '';
  }

  // Older GPU generations in a title -> today's equivalent.
  function upgradeGpuFromHay(hay, catalog) {
    var ch = compactText(hay);
    return ruleFromHay(titleRules(catalog).gpu, ch, ch.indexOf('16gb') !== -1);
  }

  function matchPsuFromHay(catalog, hay) {
    var named = matchPartFromHay(catalog.parts.psu, hay);
    if (named) return named;
    var m = hay.match(/\b(1600|1500|1200|1000|850|650|550|500)\s*w\b/);
    if (!m) return '';
    var want = Number(m[1]);
    var best = null;
    (catalog.parts.psu || []).forEach(function (p) {
      var w = psuWattsNumber(p);
      if (want >= 500 && want <= 700) {
        if (!isRole(catalog, 'range_psu', p.id)) return;
      } else if (w !== want) return;
      if (!best || Number(p.price || 0) < Number(best.price || 0)) best = p;
    });
    return best ? best.id : '';
  }

  function matchCoolerFromHay(catalog, hay) {
    var arctic = /arctic|liquid\s*freezer/.test(hay);
    var lcd = /\blcd\b|display/.test(hay);
    var rules = titleRules(catalog).cooler || {};
    var mm = hay.match(/\b(420|360|240)\s*mm\b/);
    var rad = mm && (rules.radiator || {})[mm[1]];
    if (rad) {
      if (arctic && rad.arctic) return rad.arctic;
      if (lcd && rad.lcd) return rad.lcd;
      if (rad.any) return rad.any;
    }
    if (rules.water && /water\s*cool|aio/.test(hay)) return rules.water;
    if (rules.rgb_air && /rgb\s*air|argb\s*air/.test(hay)) return rules.rgb_air;
    return matchPartFromHay(catalog.parts.cooler, hay);
  }

  function parseBomFromTitle(catalog, title) {
    var part = function (slot, id) { return partById(catalog, slot, id); };
    var hay = String(title || '').toLowerCase();
    var rules = titleRules(catalog);
    var bom = Object.assign({}, catalog.family.base_bom);
    var cpuId = matchCpuFromHay(catalog.parts.cpu, hay, catalog);
    if (cpuId) bom.cpu = cpuId;
    else if (rules.intel_base_cpu && /\bintel\b/.test(hay) && /base configuration|choose your specs|just let me game/i.test(hay)) {
      bom.cpu = rules.intel_base_cpu;
    }
    var cpu = part('cpu', bom.cpu);
    var mem = (cpu && cpu.memory) || ((cpu && (cpu.platform === 'am5' || cpu.platform === 'lga1851')) ? 'ddr5' : 'ddr4');
    if (/\bddr5\b/.test(hay)) mem = 'ddr5';
    else if (/\bddr4\b/.test(hay)) mem = 'ddr4';
    // RAM size: the first "<N>GB" in the title that is a kit the catalog sells (ids are ddr5-<N>gb), so a kit added on
    // the dashboard is read like any other. (partById never returns null, so the existence check is on the list.)
    var ramRe = /\b(\d+)\s*gb\b/g;
    var ramM;
    while ((ramM = ramRe.exec(hay))) {
      if (/\b(rtx|gtx|rx|arc)\b[\s\w]{0,14}$/.test(hay.slice(Math.max(0, ramM.index - 20), ramM.index))) continue;
      var ramId = (mem === 'ddr5' ? 'ddr5-' : 'ddr4-') + ramM[1] + 'gb';
      if ((catalog.parts.ram || []).some(function (p) { return p.id === ramId; })) { bom.ram = ramId; break; }
    }
    var ssdRe = /\b(\d+)\s*(tb|gb)\b/g;
    var ssdM;
    while ((ssdM = ssdRe.exec(hay))) {
      var ssdId = 'ssd-' + ssdM[1] + ssdM[2];
      if ((catalog.parts.ssd || []).some(function (p) { return p.id === ssdId; })) { bom.ssd = ssdId; break; }
    }
    var gpuId = matchPartFromHay(catalog.parts.gpu, hay);
    if (!gpuId) {
      var chGpu = compactText(hay);
      var gpus = (catalog.parts.gpu || []).slice().sort(function (a, b) {
        return String(b.label || '').length - String(a.label || '').length;
      });
      var wantTi = chGpu.indexOf('ti') !== -1;
      for (var gi = 0; gi < gpus.length; gi++) {
        var gcore = compactText(gpus[gi].label).replace(/\d{1,2}gbgddr\w*$/, '').replace(/\d{1,2}gb$/, '');
        if (gcore.length < 5 || chGpu.indexOf(gcore) === -1) continue;
        if (/ti/.test(gcore) && !wantTi) continue;
        if (!/ti/.test(gcore) && wantTi && /rtx\d{4}$/.test(gcore)) continue;
        gpuId = gpus[gi].id;
        break;
      }
    }
    if (!gpuId) gpuId = upgradeGpuFromHay(hay, catalog);
    if (gpuId) bom.gpu = gpuId;
    var psuId = matchPsuFromHay(catalog, hay);
    if (psuId) bom.psu = psuId;
    var coolerId = matchCoolerFromHay(catalog, hay);
    if (coolerId && part('cooler', coolerId)) bom.cooler = coolerId;
    var moboId = matchPartFromHay((catalog.parts.motherboard || []).filter(function (p) { return !isRole(catalog, 'included_board', p.id); }), hay);
    // older chipsets in a title ("Z790") -> today's board; "id_ddr5" when the title says DDR5
    (rules.motherboard || []).forEach(function (r) {
      if (moboId || !r.word || !new RegExp('\\b' + r.word + '\\b').test(hay)) return;
      moboId = (r.id_ddr5 && /\bddr5\b/.test(hay)) ? r.id_ddr5 : r.id;
    });
    // a board that needs a different CPU than the title's (e.g. a DDR5 Z790 title with a 14900K)
    (rules.board_cpu || []).forEach(function (r) {
      if (moboId !== r.board || bom.cpu !== r.cpu) return;
      bom.cpu = r.to;
      cpu = part('cpu', bom.cpu);
    });
    if (moboId) {
      var board = part('motherboard', moboId);
      if (!board.platform || board.platform === 'any' || !cpu || board.platform === cpu.platform) bom.motherboard = moboId;
    }
    var wifi = rules.wifi || {};
    if (wifi.wifi7 && /wifi\s*7/.test(hay)) bom.wifi = wifi.wifi7;
    else if (wifi.wifi && /\bwifi\b/.test(hay)) bom.wifi = wifi.wifi;
    // The operating system is NEVER read from a title (owner 2026-10-09): the word "Pro" in "Core P3 Pro" or "Pro Workstation"
    // used to switch on Windows 11 Pro (+$100). A PC's OS comes from its build code / the shopper's choice; a workstation's
    // default OS is data (gbg-dual-workstation.json "os", applied by applyDualBase).
    // Only an explicit "RGB RAM" / "ARGB RAM" / "RGB DDR5 RAM" means RGB memory - NOT the "ARGB" in a cooler
    // name ("360MM ARGB Water" used to switch on RGB RAM, +$49, on 196 products).
    if (role(catalog, 'rgb_ram') && /\ba?rgb\s+(?:ddr[45]\s+)?ram\b/.test(hay)) bom.ram_style = role(catalog, 'rgb_ram');
    return bom;
  }

  /* ---------------- Dual-GPU workstations ---------------- */

  // Adds the workstation-only parts (RAM kits, boards, network cards) to the catalog.
  function installDualBook(catalog, dualBook) {
    if (!dualBook || !catalog || !catalog.parts) return;
    var ram = catalog.parts.ram || [];
    var base = null;
    ram.forEach(function (p) { if (dualBook.ramBase && p.id === dualBook.ramBase) base = p; });
    var basePrice = base ? Number(base.price || 0) : 0;
    (dualBook.ramExtra || []).forEach(function (part) {
      if (!part || !part.id) return;
      if (!ram.some(function (p) { return p.id === part.id; })) ram.push(part);
    });
    Object.keys(dualBook.ramUpgrades || {}).forEach(function (id) {
      ram.forEach(function (p) {
        if (p.id !== id) return;
        p.price = basePrice + Number(dualBook.ramUpgrades[id]);
      });
    });
    catalog.parts.ram = ram;
    // workstation CPU prices: steps over the workstation's default CPU (defaults.cpu), like the RAM steps above
    var cpuSteps = dualBook.cpuUpgrades || {};
    if (Object.keys(cpuSteps).length) {
      var cpus = catalog.parts.cpu || [];
      var baseCpuId = (dualBook.defaults || {}).cpu;
      var baseCpu = cpus.filter(function (p) { return p.id === baseCpuId; })[0];
      if (!baseCpu) throw new Error('workstation default CPU ' + baseCpuId + ' is not in the catalog');
      var baseCpuPrice = Number(baseCpu.price || 0);
      Object.keys(cpuSteps).forEach(function (id) {
        var cpu = cpus.filter(function (p) { return p.id === id; })[0];
        if (!cpu) throw new Error('workstation CPU price for ' + id + ', which is not in the catalog');
        cpu.price = baseCpuPrice + Number(cpuSteps[id]);
      });
    }
    var boards = catalog.parts.motherboard || [];
    var extraBoards = dualBook.motherboards || (dualBook.motherboard ? [dualBook.motherboard] : []);
    extraBoards.forEach(function (board) {
      if (!board || !board.id) return;
      if (board.price == null) board.price = 0;
      if (!boards.some(function (p) { return p.id === board.id; })) boards.push(board);
    });
    catalog.parts.motherboard = boards;
    var nets = catalog.parts.wifi || [];
    (dualBook.networkParts || []).forEach(function (part) {
      if (!part || !part.id) return;
      if (part.price == null) part.price = 0;
      if (!nets.some(function (p) { return p.id === part.id; })) nets.push(part);
    });
    catalog.parts.wifi = nets;
    enforceTenDollarPrices(catalog);
  }

  function dualOfferedGpus(dualBook) {
    return (dualBook && dualBook.gpus) || [];
  }

  // The workstation's included board; without a workstation book, the gaming PCs' included board.
  function dualIncludedBoardId(dualBook, catalog) {
    if (dualBook && dualBook.includedMotherboard) return dualBook.includedMotherboard;
    return (dualBook && dualBook.motherboard && dualBook.motherboard.id) || role(catalog, 'included_board');
  }

  function dualPsuFloorWatts(dualBook, gpuId) {
    var map = (dualBook && dualBook.psuMinWatts) || {};
    var floor = Number(dualBook && dualBook.psuFloorWatts) || 0;
    return Math.max(floor, Number(map[gpuId]) || 0);
  }

  function cheapestPsuAtWatts(catalog, watts) {
    var best = null;
    (catalog.parts.psu || []).forEach(function (p) {
      if (psuWattsNumber(p) < watts) return;
      if (!best || Number(p.price || 0) < Number(best.price || 0)) best = p;
    });
    return best;
  }

  function dualWifiFor(catalog, boardId) {
    var board = partById(catalog, 'motherboard', boardId);
    if (board && board.wifi) return board.wifi;
    return role(catalog, 'no_wifi');
  }

  // Workstation defaults on top of the title's parts (gbg-dual-workstation.json "defaults": Intel Ultra, DDR5,
  // offered GPUs, ARGB air, included board, PSU floor for the GPU, Windows Pro, board networking).
  function applyDualBase(catalog, dualBook, bom) {
    if (!bom) return bom;
    var d = (dualBook && dualBook.defaults) || {};
    var listed = function (list, id) { return (list || []).indexOf(id) !== -1; };
    var cpu = partById(catalog, 'cpu', bom.cpu);
    if (d.cpu && (!cpu || (cpu.brand !== 'intel' && !listed(dualBook && dualBook.cpus, cpu.id)))) bom.cpu = d.cpu;
    if (d.ram && (!bom.ram || listed(d.ramReplaces, bom.ram))) bom.ram = d.ram;
    if (d.ssd && !bom.ssd) bom.ssd = d.ssd;
    if (d.gpu && dualOfferedGpus(dualBook).indexOf(bom.gpu) === -1) bom.gpu = d.gpu;
    if (d.cooler && (!bom.cooler || listed(d.coolerReplaces, bom.cooler))) bom.cooler = d.cooler;
    // the workstation opens on its CPU platform's default board (defaults.boardByPlatform: LGA1851 Z890 Aero G, AM5 ProArt B850);
    // a board the product's own parts name is kept
    var cpuNow = partById(catalog, 'cpu', bom.cpu);
    var openOn = (cpuNow && (d.boardByPlatform || {})[cpuNow.platform]) || dualIncludedBoardId(dualBook, catalog);
    if (!bom.motherboard || isRole(catalog, 'included_board', bom.motherboard)) {
      bom.motherboard = openOn;
    }
    var floor = dualPsuFloorWatts(dualBook, bom.gpu);
    var psu = partById(catalog, 'psu', bom.psu);
    if (!psu || isRole(catalog, 'range_psu', psu.id) || psuWattsNumber(psu) < floor) {
      var nextPsu = cheapestPsuAtWatts(catalog, floor);
      if (nextPsu) bom.psu = nextPsu.id;
    }
    if (dualBook && dualBook.os) bom.os = dualBook.os;
    bom.wifi = dualWifiFor(catalog, bom.motherboard);
    return bom;
  }

  return {
    applyPartPrices: applyPartPrices,
    role: role,
    isRole: isRole,
    titleRules: titleRules,
    catalogSlot: catalogSlot,
    partById: partById,
    psuWattsNumber: psuWattsNumber,
    compactText: compactText,
    matchPartFromHay: matchPartFromHay,
    matchCpuFromHay: matchCpuFromHay,
    upgradeGpuFromHay: upgradeGpuFromHay,
    matchPsuFromHay: matchPsuFromHay,
    matchCoolerFromHay: matchCoolerFromHay,
    parseBomFromTitle: parseBomFromTitle,
    installDualBook: installDualBook,
    dualOfferedGpus: dualOfferedGpus,
    dualIncludedBoardId: dualIncludedBoardId,
    dualPsuFloorWatts: dualPsuFloorWatts,
    cheapestPsuAtWatts: cheapestPsuAtWatts,
    dualWifiFor: dualWifiFor,
    applyDualBase: applyDualBase
  };
});
